import { Head, Link, useForm, usePage } from '@inertiajs/react'
import type { ReactElement } from 'react'
import { Building2, Folder, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { SharedProps } from '@/frontend/types/inertia'

interface Props {
  destination_organization: string
  destination_project: string | null
  cancel_path: string
  confirmation_token: string
  copy: Record<'title' | 'description' | 'organization' | 'project' | 'open' | 'cancel', string>
}

/** Permalink gate: no destination content is loaded before consent. */
export default function ContextSwitch({ destination_organization, destination_project, cancel_path, confirmation_token, copy }: Props) {
  const form = useForm({ token: confirmation_token })
  const { csrfToken } = usePage<SharedProps>().props
  return <main className="flex min-h-svh items-center justify-center bg-muted/30 px-4">
    <Head title={copy.title} />
    <section aria-labelledby="context-switch-title" className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-sm">
      <h1 id="context-switch-title" className="text-lg font-semibold tracking-tight">{copy.title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{copy.description}</p>
      <dl className="my-6 space-y-4 text-sm">
        <div><dt className="mb-1 text-xs text-muted-foreground">{copy.organization}</dt><dd className="flex items-center gap-2 font-medium"><Building2 className="size-4 text-muted-foreground" />{destination_organization}</dd></div>
        {destination_project && <div><dt className="mb-1 text-xs text-muted-foreground">{copy.project}</dt><dd className="flex items-center gap-2 font-medium"><Folder className="size-4 text-muted-foreground" />{destination_project}</dd></div>}
      </dl>
      <form onSubmit={(event) => {
        event.preventDefault()
        // Use this page's Rails token even if another localhost app replaced XSRF-TOKEN.
        form.transform(() => ({ token: confirmation_token, authenticity_token: csrfToken }))
        form.post('/dashboard/context_switch')
      }} className="flex justify-end gap-2">
        <Button variant="outline" asChild><Link href={cancel_path}>{copy.cancel}</Link></Button>
        <Button type="submit" disabled={form.processing}>{copy.open}<ArrowRight className="size-4" /></Button>
      </form>
    </section>
  </main>
}

// Keep the old tenant's sidebar out of this gate while the URL names the new one.
ContextSwitch.layout = (page: ReactElement) => page
